import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsMore from 'highcharts/highcharts-more';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import {
	buildYAxisConfig,
	buildSeriesData,
	hexToRgba,
	generateColor,
} from '@/utils/DashboardGraphs';
import DashboardContext from '@/contexts/DashboardContext';
import { useDashboardDataContext } from '@/components/Dashboard/DashboardDataContext';
import { getUserTimezoneOffset } from '@/utils/dateTimeUtils';

HighchartsMore(Highcharts);


export function useGraphBuilder({
	chartRef,
	strategiesChartRef,
	readingsList,
	pastForecastData,
	expanded = false,
	annotationMode = false,
	onClickChart = (event: any) => {},
	onRangeSelected = (event: any) => {},
}) {
	const { value: context, setValue: setContext } = useContext(DashboardContext);
	const { seriesVisibilityState } = useDashboardDataContext();
	const [plotbands, setPlotbands] = useState<any[]>([]);

	// ✅ Update chart click event when onClickChart changes
	useEffect(() => {
		const chart = chartRef?.current?.chart;
		if (!chart) return;

		// Update the click event handler
		chart.update({
			chart: {
				events: {
					click: function (event: any) {
						if (onClickChart) {
							onClickChart(event);
						}
					},
				},
			},
		}, false); // false = no redraw
	}, [onClickChart, chartRef]);

	const buildGraphOptions = useCallback(
		(dataArray, pastForecastData, seriesVisibilityState = new Map()) => {
			// Filter dataArray to keep only elements with distinct parameterId and sourceSerialNumber
			const uniqueMap = new Map();
			dataArray = dataArray.filter((item) => {
				const key = `${item.originalParameterId}-${item.originalSourceSerialNumber}`;
				if (!uniqueMap.has(key)) {
					uniqueMap.set(key, true);
					return true;
				}
				return false;
			});

			const alreadyPlotbands = plotbands.filter((pb) => {
				return dataArray.some((obj) => {
					// Case 1: Regular plotband (sourceId matches exactly)
					const regularMatch =
						obj.originalParameterId == pb.parameterId && obj.originalSourceId == pb.sourceId;

					// Case 2: Note plotband (sourceId can be different but parameterId matches)
					// Notes have groupId (UUID) instead of annotationId
					const isNote = pb.id && pb.id.includes('-') && pb.id.length > 30; // UUID format
					const noteMatch = isNote && obj.originalParameterId == pb.parameterId;

					return regularMatch || noteMatch;
				});
			});
			const yAxis = buildYAxisConfig(dataArray);
			if (yAxis.length === 0) {
				return null;
			}
			let series: any = buildSeriesData(dataArray, yAxis);

			// Apply visibility state to series
			series = series.map((serie) => {
				const paramId = serie.paramId;
				const isVisible =
					seriesVisibilityState.has(paramId) && serie.sourceSerialNumber === null
						? seriesVisibilityState.get(paramId)
						: true; // visible by default

				return {
					...serie,
					visible: isVisible,
				};
			});

			const [dateFrom, dateTo] = context.selectedDate;
			// Extract local time components and treat as UTC to match chart data format
			const dFrom = dayjs(dateFrom);
			const dTo = dayjs(dateTo);
			let initialMin = Date.UTC(dFrom.year(), dFrom.month(), dFrom.date(), dFrom.hour(), dFrom.minute(), dFrom.second());
			let initialMax = Date.UTC(dTo.year(), dTo.month(), dTo.date(), dTo.hour(), dTo.minute(), dTo.second());

			// Only show forecast when ONLY recommended sources are visible (no other sources)
			const recommendedSeries = series.filter(
				(serie) => serie.sourceSerialNumber === null && serie.visible,
			);
			const nonRecommendedSeries = series.filter(
				(serie) => serie.sourceSerialNumber !== null && serie.visible,
			);
			const shouldShowForecast = recommendedSeries.length > 0 && nonRecommendedSeries.length === 0;

			// Remove existing forecast series first
			series = series.filter((serie) => serie.type !== 'arearange');

			if (pastForecastData && pastForecastData.length > 0 && shouldShowForecast) {
				const forecastSeries = pastForecastData
					.flat(2)
					.map((item) => ({
						x: item.time,
						low: item.minValue,
						high: item.maxValue,
					}))
					.filter((pt) => pt.x && pt.low !== undefined && pt.high !== undefined);

				series.push({
					type: 'arearange',
					lineWidth: 2,
					color: Highcharts.getOptions().colors?.[0] ?? '#7cb5ec',
					fillOpacity: 0.5,
					linkedTo: ':previous',
					zIndex: 0,
					marker: { enabled: false },
					data: forecastSeries,
					unit: dataArray[0]?.valueUnit ?? '',
					paramId: dataArray[0]?.measurementTargetId,
					sourceSerialNumber: null,
					isForecast: false,
				});

				const forecastXs = forecastSeries.map((pt) => pt.x);
				initialMin = Math.min(initialMin, ...forecastXs);
				initialMax = Math.max(initialMax, ...forecastXs);
			}


			return {
				title: { text: '' },
				credits: { enabled: false },
				chart: {
					type: 'line',
					animation: false,
					zoomType: 'x', // 
					style: {
						cursor: annotationMode ? 'crosshair' : 'default',
					},
					cursor: annotationMode ? 'crosshair' : 'default',
					events: {
						click: function (event: any) {
							if (onClickChart) {
								onClickChart(event);
							}
						},
						selection: function (event: any) {
							if (annotationMode) {
								//  In annotation mode, use for range selection but prevent zoom
								if (onRangeSelected) {
									onRangeSelected(event);
								}
								return false; // ✅ Prevent zoom but allow selection
							}
							// ✅ In normal mode, allow zoom and also call onRangeSelected
							if (onRangeSelected) {
								onRangeSelected(event);
							}
							return true;
						},
					},
					resetZoomButton: {
						position: {
							align: 'right',
							verticalAlign: 'top',
							x: -10,
							y: 10,
						},
						theme: {
							fill: 'white',
							stroke: 'silver',
							r: 0,
							states: {
								hover: {
									fill: '#f7f7f7',
									style: {
										color: 'black',
									},
								},
							},
						},
					},
					panKey: 'shift',
					height: null, // ✅ Adaptable height for both views
					plotBorderWidth: 1,
					backgroundColor: '#FFFFFF',
					reflow: true,
				},
				legend: { enabled: false },
				xAxis: {
					type: 'datetime',
					min: initialMin,
					max: initialMax,
					dateTimeLabelFormats: {
						millisecond: '%l:%M:%S%p',
						second: '%l:%M:%S%p',
						minute: '%l:%M%p',
						hour: '%l:%M%p',
						day: '%e. %b',
						week: '%e. %b',
						month: "%b '%y",
						year: '%Y',
					},
					plotBands: [
						{
							from: new Date().getTime() + (getUserTimezoneOffset() ?? 0) * 3600000,
							to: initialMax,
							color: '#EFFFFF',
						},
						...alreadyPlotbands,
					],
					ordinal: false,
					minRange: 60 * 1000, // ✅ Minimum range of 1 minute
					animation: false, // ✅ No animation to avoid positioning issues
					events: {
						afterSetExtremes: function (event) {
							const min = event.min;
							const max = event.max;
							const range = max - min;

							const startDate = dayjs(min);
							const endDate = dayjs(max);
							const diffInMonths = endDate.diff(startDate, 'month', true);
							let newUnits;
							if (diffInMonths < 1) {
								newUnits = [['minute', [5]]];
							} else if (diffInMonths >= 1 && diffInMonths <= 6) {
								newUnits = [['minute', [15]]];
							} else {
								newUnits = [['hour', [1]]];
							}
						},
					},
				},
				plotOptions: {
					series: {
						animation: false, // ✅ No animation for better performance and stability
						connectNulls: true,
						label: {
							connectorAllowed: true,
						},
						turboThreshold: 0,
						boostThreshold: 5000, // Only activate boost for series with 5000+ points (boost disables SVG markers)
						marker: {
							enabled: null, // null = auto (Highcharts shows markers when zoomed in enough)
							radius: 3,
							states: {
								hover: {
									enabled: true,
									radius: 5,
								},
							},
						},
						cursor: annotationMode ? 'crosshair' : 'default', // ✅ Cursor in the graph area
						enableMouseTracking: true, // ✅ Ensure mouse tracking is enabled
					},
				},
				// Boost configuration — only for large datasets (boost disables SVG markers/points)
				boost: {
					useGPUTranslations: true,
					seriesThreshold: 5, // Only activate boost when 5+ series are present
				},
				navigator: {
					enabled: true,
					adaptToUpdatedData: true,
					stickToMax: false,
					height: 30,
				},
				tooltip: {
					formatter: function (this: any) {
						try {
							const vm: any = this;
							const hoveredSeries = this.series;
							
							// ✅ Strategies (xrange) - tooltip específico
							if (hoveredSeries?.type === 'xrange') {
								const point = this.point;
								return `
									<b>${point.inpName || 'Strategy'}</b><br/>
									<span style="color:${point.color}">●</span> <b>${hoveredSeries.name}</b><br/>
									<b>From:</b> ${Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', point.x)}<br/>
									<b>To:</b> ${Highcharts.dateFormat('%Y-%m-%d %H:%M:%S', point.x2)}<br/>
								`;
							}
							
							// ✅ Readings (line series)
						// Validate that vm.points exists and has elements
						if (!vm.points || vm.points.length === 0) {
							return '';
						}
						
						let arearangeTooltip = '';
						try {
							vm.series.chart.series
								.filter((s) => s.type === 'arearange')
								.forEach((s) => {
									const pt = s.data.filter((d) => d.x).find((d) => d.x === vm.x);
									if (pt) {
										arearangeTooltip = `
				                <br/><b>Max:</b> ${pt.high.toFixed(3)} ${pt.series.userOptions.unit}<br/>
				                <b>Min:</b> ${pt.low.toFixed(3)} ${pt.series.userOptions.unit}<br/>`;
									}
								});
						} catch (e) {
							// Ignore arearange errors
						}

						let text = `<span>${dayjs.utc(vm.points[0].x).format(
							'YYYY-MM-DD hh:mm:ss A',
						)}</span><br/><br/>`;

						// Limit to maximum 15 points to avoid long tooltips
						const maxPoints = 15;
						const pointsToShow = vm.points.slice(0, maxPoints);
						const hasMore = vm.points.length > maxPoints;

						pointsToShow.forEach((point: any) => {
								try {
									const { series: seriesData, point: pointData } = point;
									if (!seriesData || !pointData) return;
									
									const { userOptions } = seriesData;
									if (!userOptions) return;
									
									const symbol =
										userOptions.isForecast && userOptions.isPluviometer
											? userOptions.forecastIndex == 1
												? '◆'
												: userOptions.forecastIndex == 2
													? '■'
													: '▲'
											: '●';
									if (pointData.origen) {
										if (userOptions.isForecast) {
											text += `<span style="color:${seriesData.color}">${symbol}${userOptions.name} ${userOptions.parameterName} </span>: <b>${point.y} ${userOptions.valueUnit}</b><br/>
		                         <span> Iteration ${userOptions.indexIteration}</b></span><br/>`;
											if (pointData.measurementToolForecastDetails) {
												text += `<span> Strategy: ${pointData.measurementToolForecastDetails}</b></span><br/>`;
											}
										} else {
											text += `<span style="color:${seriesData.color}">${symbol} ${userOptions.name} ${userOptions.parameterName} </span>: <b>${point.y} ${userOptions.valueUnit}</b><br/>
											<span> Source: <b>${pointData.origen}</b></span><br/>
											<span> Quality: <b>${pointData.quality}</b></span><br/> `;
										}
									}
								} catch (e) {
									// Skip problematic points
						}
					});
					
					// ✅ Add message if there are more series
					if (hasMore) {
						const remaining = vm.points.length - maxPoints;
						text += `<br/><i style="color: #666;">... and ${remaining} more</i>`;
					}
					
					text += arearangeTooltip;
					return text;
						} catch (error) {
							console.error('Tooltip formatter error:', error);
							return '';
						}
					},
					useHtml: true,
					outside: true,
					backgroundColor: '#fff',
					borderWidth: 0,
					borderRadius: 8,
					shadow: true,
					style: {
						maxHeight: '400px',
						overflowY: 'auto',
						pointerEvents: 'none',
						zIndex: 9999,
						position: 'fixed',
					},
					positioner: function (labelWidth, labelHeight, point) {
					// eslint-disable-next-line @typescript-eslint/no-this-alias
					const vm: any = this;
					const chart = vm.chart;

					const plotHeight = chart.plotHeight;
					const plotWidth = chart.plotWidth;
					const plotTop = chart.plotTop;
					const plotLeft = chart.plotLeft;

					// Determine position relative to plot area
					const isInUpperHalf = point.plotY < plotHeight / 2;
					const isInRightHalf = point.plotX > plotWidth / 2;

					// Calculate available space in all directions
					const spaceRight = plotWidth - point.plotX;
					const spaceLeft = point.plotX;
					const spaceAbove = point.plotY;
					const spaceBelow = plotHeight - point.plotY;

					let tooltipX, tooltipY;

					// HORIZONTAL POSITIONING
					// Check if tooltip fits on the right side
					if (spaceRight > labelWidth + 20) {
						// Tooltip to the right of point
						tooltipX = point.plotX + plotLeft + 15;
					} else if (spaceLeft > labelWidth + 20) {
						// Tooltip to the left of point
						tooltipX = point.plotX + plotLeft - labelWidth - 15;
					} else {
						// Not enough space on either side, center it
						tooltipX = plotLeft + (plotWidth - labelWidth) / 2;
					}

					// VERTICAL POSITIONING
					// When point is near the right edge, consider vertical position more carefully
					const isNearRightEdge = spaceRight < labelWidth + 20;
					
					if (isNearRightEdge) {
						// Point near right edge: position based on vertical space
						if (isInUpperHalf && spaceBelow > labelHeight + 30) {
							// Upper half + enough space below → tooltip below
							tooltipY = point.plotY + plotTop + 20;
						} else if (!isInUpperHalf && spaceAbove > labelHeight + 30) {
							// Lower half + enough space above → tooltip above
							tooltipY = point.plotY + plotTop - labelHeight - 10;
						} else if (spaceBelow > spaceAbove) {
							// Use side with more space (below)
							tooltipY = point.plotY + plotTop + 20;
						} else {
							// Use side with more space (above)
							tooltipY = point.plotY + plotTop - labelHeight - 10;
						}
					} else {
						// Point not near edge: use standard logic
						if (isInUpperHalf) {
							// Point in upper half → tooltip below
							tooltipY = point.plotY + plotTop + 20;
						} else {
							// Point in lower half → tooltip above
							tooltipY = point.plotY + plotTop - labelHeight - 10;
						}
					}

					// Final boundary checks to ensure tooltip stays within chart
					const containerWidth = chart.chartWidth;
					const containerHeight = chart.chartHeight;

					// Horizontal boundaries
					if (tooltipX < 10) {
						tooltipX = 10;
					} else if (tooltipX + labelWidth > containerWidth - 10) {
						tooltipX = containerWidth - labelWidth - 10;
					}

					// Vertical boundaries
					if (tooltipY < 10) {
						tooltipY = 10;
					} else if (tooltipY + labelHeight > containerHeight - 10) {
						tooltipY = containerHeight - labelHeight - 10;
					}

					return { x: tooltipX, y: tooltipY };
				},
					valueDecimals: 2,
					shared: true,
					xDateFormat: '%Y-%m-%d %H:%M:%S',
				},
				rangeSelector: {
					buttonTheme: {
						style: {
							display: 'none',
						},
					},
					enabled: false,
				},
				yAxis,
				series,
			};
		},
		[buildYAxisConfig, buildSeriesData, context.selectedDate, plotbands, annotationMode, expanded],
	);

	const graphOptions = useMemo(() => {
		const res = buildGraphOptions(readingsList, pastForecastData, seriesVisibilityState);
		return res;
	}, [readingsList, pastForecastData, seriesVisibilityState, buildGraphOptions]);

	/**
	 * Removes specific plot bands by their IDs
	 */
	const removePlotBandsByIds = useCallback((ids: string[]) => {
		const chart = chartRef?.current?.chart;
		const strategiesChart = strategiesChartRef?.current?.chart;

		const activeChart = chart || strategiesChart;
		
		if (!activeChart) {
			return;
		}

		let removedCount = 0;

		ids.forEach(id => {
			activeChart.xAxis.forEach((axis, axisIndex) => {
				try {
					const existingBands = axis.plotLinesAndBands || [];
					const bandExists = existingBands.some((band: any) => band.id === id);
					if (bandExists) {
						axis.removePlotBand(id);
						removedCount++;
					}
				} catch (e) {
					// Plotband might not exist, ignore
				}
			});

			activeChart.yAxis.forEach((axis, axisIndex) => {
				try {
					const existingBands = axis.plotLinesAndBands || [];
					const bandExists = existingBands.some((band: any) => band.id === id);
					if (bandExists) {
						axis.removePlotBand(id);
						removedCount++;
					}
				} catch (e) {
					// Plotband might not exist, ignore
				}
			});
		});

		// ✅ CRÍTICO: También remover del estado plotbands
		setPlotbands((prev) => {
			const filtered = prev.filter((pb) => !ids.includes(pb.id));
			return filtered;
		});

		if (strategiesChart && strategiesChart !== activeChart) {
			ids.forEach(id => {
				strategiesChart.xAxis.forEach((axis) => {
					try {
						axis.removePlotBand(id);
					} catch (e) {
						// Ignore
					}
				});
				strategiesChart.yAxis.forEach((axis) => {
					try {
						axis.removePlotBand(id);
					} catch (e) {
						// Ignore
					}
				});
			});
		}
	}, []);

	/**
	 * Removes all plot bands from the graph
	 */

	const removeAllPlotBands = useCallback(() => {
		const chart = chartRef?.current?.chart;
		const strategiesChart = strategiesChartRef?.current?.chart;

		// Try to use whichever chart is available
		const activeChart = chart || strategiesChart;
		
		if (!activeChart) {
			return;
		}

		// Remove plotbands from active chart
		activeChart.yAxis.forEach((axis) => {
			const bands = [...axis.plotLinesAndBands];
			bands.forEach((band) => {
				if (band.id) {
					axis.removePlotBand(band.id);
				}
			});
		});

		activeChart.xAxis.forEach((axis) => {
			const bands = [...axis.plotLinesAndBands];
			bands.forEach((band) => {
				if (band.id) {
					axis.removePlotBand(band.id);
				}
			});
		});
		
		// ✅ Clear plotbands state
		setPlotbands([]);

		// Remove plotbands from the OTHER chart if both exist and we used main chart
		if (chart && strategiesChart && activeChart === chart) {
			strategiesChart.yAxis.forEach((axis) => {
				const bands = [...axis.plotLinesAndBands];
				bands.forEach((band) => {
					if (band.id) {
						axis.removePlotBand(band.id);
					}
				});
			});

			strategiesChart.xAxis.forEach((axis) => {
				const bands = [...axis.plotLinesAndBands];
				bands.forEach((band) => {
					if (band.id) {
						axis.removePlotBand(band.id);
					}
				});
			});
		}
	}, []);

	// Function to add annotation bands
	const addPlotBand = useCallback(
		(annotation: any) => {
			const chart = chartRef.current?.chart;
			const strategiesChart = strategiesChartRef.current?.chart;

			// Only validate main chart - strategies chart is optional
			if (!chart) {
				return;
			}

			// Generate unique ID for the plotband
			const id = annotation.groupId ?? `annotation-${annotation.annotationId}`;
			const isNote = annotation.groupId !== undefined && annotation.groupId !== null;

			const xAxis = chart.xAxis[0];

			// Remove band if it already exists from main chart
			xAxis.removePlotBand(id);

			setPlotbands((prev) => {
				return prev.filter((p) => p.id != id);
			});

			const offsetInMs = dayjs().utcOffset() * 60 * 1000;

			const plotband = {
				id: id,
				parameterId: annotation.parameterId,
				sourceId: annotation.sourceId,
				from: dayjs(annotation.startDate).valueOf() + offsetInMs,
				to: dayjs(annotation.endDate).valueOf() + offsetInMs,
				color: annotation.groupId !== undefined && annotation.groupId !== null
					? 'rgba(128,128,128, 0.3)'
					: hexToRgba(generateColor(annotation.parameterId, annotation.serialNumber), 0.2),
				zIndex: isNote ? 1 : 0,
				className: isNote ? 'highcharts-plot-band-note' : 'plotband-no-pointer-events',
			};

			setPlotbands((prev) => {
				return [...prev, plotband];
			});

			// Add new band to main chart
			xAxis.addPlotBand(plotband);

			// ✅ Add to strategies chart if it exists
			// if (strategiesChart) {
			// 	const strategiesXAxis = strategiesChart.xAxis[0];
			// 	strategiesXAxis.addPlotBand(plotband);
			// }

			// ✅ Optimized redraw with fast animation
			if (chartRef.current?.chart) {
				chartRef.current.chart.redraw();
			}
			// if (strategiesChart) {
			// 	strategiesChart.redraw();
			// }
		},
		[chartRef.current],
	);

	return {
		buildGraphOptions,
		graphOptions,
		addPlotBand,
		removeAllPlotBands,
		removePlotBandsByIds,
	};
}
